import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  UserCircle,
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Eye,
  Filter,
  FileText,
  FileSpreadsheet,
  RefreshCcw,
  LayoutGrid,
  Shield,
  Layers,
  Users,
  Briefcase,
  Home,
  Wallet,
  Calendar,
  FileCheck,
  Phone,
  Mail,
  MapPin,
  Building2,
  Award,
  Download,
  Upload,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileOutput
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import {
  formatHrDate,
  formatHrPhone,
  getHrPhonePlaceholder,
  getDefaultSaudiEmployeeFields,
} from './utils/hrSaudiFormat';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import { useHrModalBodyLock, buildHrColumnVisibilityState, buildHrQawaemColumnDefs, isHrColumnVisible, hrTableColSpan, HrFormModalTabBar, useHrTableRowSelection, useHrEmployees, previewHrEmployeeMatricule } from './hrListPageUtils';
import { useHrConfig } from './useHrConfig';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import localApiAdapter from '../../core/services/localApiAdapter';
import { dispatchHrEmployeesRefresh } from './hrRefreshEvents';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyDeleted, notifyError } from '../../shared/utils/crudNotify';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
// Tables-mobile.css - DELETED

const HR_TABLE_COLUMNS = [
  { key: 'matricule', labelKey: 'col_matricule', required: true },
  { key: 'fullName', labelKey: 'col_full_name', required: true },
  { key: 'function', labelKey: 'col_function' },
  { key: 'gender', labelKey: 'col_gender' },
  { key: 'situation', labelKey: 'col_situation' },
  { key: 'contact', labelKey: 'col_contact' },
  { key: 'assignment', labelKey: 'col_assignment' },
  { key: 'actions', labelKey: 'col_actions', required: true },
];

const EmployeeInfoManagementPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.employeeInfoManagementPage.${key}`, opts);
  const phonePlaceholder = getHrPhonePlaceholder();
  const { employees, refresh: refreshEmployees } = useHrEmployees();
  const hrConfig = useHrConfig();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [formTab, setFormTab] = useState('personal');
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [mainViewMode, setMainViewMode] = useState('all_info');
  const [quickFilter, setQuickFilter] = useState('all');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  useHrModalBodyLock(showModal, showViewModal);

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingEmployee(null);
  };

  // Dropdown states and refs
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  const stats = {
    total: employees.length,
    hommes: employees.filter(e => e.sexe === 'M').length,
    femmes: employees.filter(e => e.sexe === 'F').length,
    marie: employees.filter(e => e.situationFamiliale === 'Marie(e)').length,
    celibataire: employees.filter(e => e.situationFamiliale === 'Celibataire').length,
    departments: [...new Set(employees.map(e => e.affectation))].length
  };

  const employeeDistribution = useMemo(() => ({
    all_info: {
      title: p('dist_overview'),
      data: [
        { filterKey: 'hommes', name: p('dist_men'), count: stats.hommes, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'femmes', name: p('dist_women'), count: stats.femmes, color: 'bg-gradient-to-r from-pink-500 to-pink-600' },
        { filterKey: 'marie', name: p('dist_married'), count: stats.marie, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'celibataire', name: p('dist_single'), count: stats.celibataire, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ],
    },
    par_genre: {
      title: p('dist_by_gender'),
      data: [
        { filterKey: 'hommes', name: p('dist_men'), count: stats.hommes, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'femmes', name: p('dist_women'), count: stats.femmes, color: 'bg-gradient-to-r from-pink-500 to-pink-600' },
      ],
    },
    par_situation: {
      title: p('dist_by_situation'),
      data: [
        { filterKey: 'marie', name: p('dist_married'), count: stats.marie, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'celibataire', name: p('dist_single'), count: stats.celibataire, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        {
          filterKey: 'autres',
          name: p('dist_other'),
          count: employees.filter(e => e.situationFamiliale !== 'Marie(e)' && e.situationFamiliale !== 'Celibataire').length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600',
        },
      ],
    },
  }), [employees, stats, t]);

  const distributionInfo = employeeDistribution[mainViewMode] || employeeDistribution.all_info;

  const mainTabs = useMemo(() => [
    { id: 'all_info', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_genre', name: p('tab_by_gender'), icon: Users },
    { id: 'par_situation', name: p('tab_by_situation'), icon: Home },
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Users },
    { id: 'hommes', name: p('filter_men'), icon: UserCircle },
    { id: 'femmes', name: p('filter_women'), icon: UserCircle },
    { id: 'marie', name: p('filter_married'), icon: Home },
  ], [t]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredEmployees = useMemo(() => employees.filter((employee) => {
    const matchesSearch = !searchTerm ||
      employee.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.mat?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.fonction?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (quickFilter === 'hommes' && employee.sexe !== 'M') return false;
    if (quickFilter === 'femmes' && employee.sexe !== 'F') return false;
    if (quickFilter === 'marie' && employee.situationFamiliale !== 'Marie(e)') return false;

    if (departmentFilter !== 'all' && employee.affectation !== departmentFilter) return false;

    return true;
  }), [employees, searchTerm, quickFilter, departmentFilter]);

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredEmployees, selectedEmployees, setSelectedEmployees);

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(HR_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_delete_bulk', { count: selectedEmployees.length }))) return;
    const deleteCount = selectedEmployees.length;
    try {
      await Promise.all(selectedEmployees.map((id) => localApiAdapter.delete(`/hr/employees/${id}`)));
      setSelectedEmployees([]);
      dispatchHrEmployeesRefresh();
      await refreshEmployees();
      notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'موظف' }));
    } catch (error) {
      console.error('Error bulk deleting employees:', error);
      notifyError(showNotify, p('confirm_delete_one'));
    }
  };

  const handleView = (employee) => {
    setViewingEmployee(employee);
    setShowViewModal(true);
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormTab('personal');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('confirm_delete_one'))) return;
    try {
      await localApiAdapter.delete(`/hr/employees/${id}`);
      dispatchHrEmployeesRefresh();
      await refreshEmployees();
      notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الموظف' }));
    } catch (error) {
      console.error('Error deleting employee:', error);
      notifyError(showNotify, p('confirm_delete_one'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = filteredEmployees.map(e => ({
        Matricule: e.mat,
        Nom: e.nom,
        Prenom: e.prenom,
        Fonction: e.fonction,
        Genre: e.sexe,
        Situation: e.situationFamiliale,
        Telephone: e.telephone || p('na'),
        Email: e.email || p('na')
      }));
      await generateReportPDF(p('report_list'), reportData, { orientation: 'landscape' });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = filteredEmployees.map(e => ({
        [p('col_matricule')]: e.mat,
        [p('export_col_last_name')]: e.nom,
        [p('export_col_first_name')]: e.prenom,
        [p('export_col_birth_date')]: e.dateNaissance,
        [p('export_col_birth_place')]: e.lieuNaissance,
        [p('export_col_gender')]: e.sexe,
        [p('export_col_marital_status')]: e.situationFamiliale,
        [p('export_col_children')]: e.enfants || 0,
        [p('export_col_address')]: e.adresse,
        [p('export_col_city')]: e.ville,
        [p('export_col_postal_code')]: e.codePostal,
        [p('export_col_phone')]: e.telephone || p('na'),
        [p('export_col_email')]: e.email || p('na'),
        [p('col_function')]: e.fonction,
        [p('export_col_position')]: e.posteOccupe,
        [p('col_assignment')]: e.affectation,
        [p('export_col_recruitment_date')]: e.dateRecrutement
      }));
      await generateReportExcel(p('report_list'), reportData);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const getGenderBadge = (sexe) => {
    if (sexe === 'M') return <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1.5"><UserCircle size={14} />{p('gender_male')}</span>;
    if (sexe === 'F') return <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-pink-500/20 text-pink-400 border border-pink-500/30 flex items-center gap-1.5"><UserCircle size={14} />{p('gender_female')}</span>;
    return <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">{p('na')}</span>;
  };

  const employeeInfoFormTabs = useMemo(() => ([
    { id: 'personal', label: p('tab_personal', { defaultValue: 'المعلومات الشخصية' }), icon: UserCircle, iconClassName: 'text-blue-400' },
    { id: 'details', label: p('tab_contact', { defaultValue: 'الاتصال والامتثال' }), icon: Phone, iconClassName: 'text-amber-400' },
  ]), [p]);

  const closeEmployeeModal = () => {
    setShowModal(false);
    setEditingEmployee(null);
    setFormTab('personal');
  };

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <UserCircle className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button type="button" onClick={() => void refreshEmployees()} className="refresh-button" title={p('refresh_title')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">
            {p('subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`} title={p('advanced_options')}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        <button
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          onClick={() => {
            setEditingEmployee(null);
            setFormTab('personal');
            setShowModal(true);
          }}
        >
          <Plus className="h-5 w-5" />
          <span>{p('add_employee')}</span>
        </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: Users, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_men'), value: stats.hommes, icon: UserCircle, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_women'), value: stats.femmes, icon: UserCircle, color: 'from-pink-500 to-pink-600' },
          { title: p('stat_married'), value: stats.marie, icon: Home, color: 'from-green-500 to-green-600' },
          { title: p('stat_single'), value: stats.celibataire, icon: Home, color: 'from-orange-500 to-orange-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative">
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <stat.icon className="h-6 w-6 text-blue-400 shrink-0" aria-hidden />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-6 pt-0">
      {/* Main View Tabs */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setCurrentPage(1); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}>
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Quick Filter Buttons */}
      {mainViewMode === 'all_info' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button key={filter.id} onClick={() => { setQuickFilter(filter.id); setCurrentPage(1); }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}>
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Distribution */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
            <div key={item.filterKey || item.name} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-white">{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${(() => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                })()}%` }}></div>
              </div>
              <p className="mt-2 text-sm text-white/60">
                {p('of_employees_pct', {
                  pct: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })(),
                })}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
        </div>
      </motion.div>

      {/* Employee Contracts Table with Integrated Header */}
      <div className="glass-panel glass-table-shell overflow-visible">
          {/* Bulk Actions Row - Show when items selected */}
          {selectedEmployees.length > 0 && (
            <div className="flex items-center gap-2 bg-white/10 py-2 px-4 rounded-xl mt-4">
              <span className="text-white font-medium">{p('bulk_selected', { count: selectedEmployees.length })}</span>
              <button onClick={handleBulkDelete} className="bg-red-500/20 text-red-400 p-2 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-1">
                <Trash2 size={18} /><span>{p('delete')}</span>
              </button>
              <button onClick={() => setSelectedEmployees([])} className="bg-white/10 text-white/70 p-2 rounded-lg hover:bg-white/20 transition-colors">{p('cancel')}</button>
            </div>
          )}

        <div className="overflow-x-auto table-horizontal-scroll">
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                  <div className="flex justify-center">
                    <QawaemTableCheckbox
                      size="lg"
                      checked={allRowsSelected}
                      indeterminate={someRowsSelected}
                      onChange={handleSelectAll}
                    />
                  </div>
                </th>
                {isColVisible('matricule') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_matricule')}</th>}
                {isColVisible('fullName') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_full_name')}</th>}
                {isColVisible('function') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_function')}</th>}
                {isColVisible('gender') && <th className="px-6 py-4 text-center text-white font-semibold">{p('col_gender')}</th>}
                {isColVisible('situation') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_situation')}</th>}
                {isColVisible('contact') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_contact')}</th>}
                {isColVisible('assignment') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_assignment')}</th>}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={qawaemColumns}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((employee, index) => (
                  <tr key={employee.id}
                    className={`hover:bg-white/5 transition-colors ${selectedEmployees.includes(employee.id) ? 'bg-white/10' : ''}`}>
                    <td className="px-3 py-4 text-center align-middle" data-label={p('col_select')}>
                      <div className="flex justify-center">
                        <QawaemTableCheckbox
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={() => handleSelectRecord(employee.id)}
                        />
                      </div>
                    </td>
                    {isColVisible('matricule') && <td className="px-6 py-4 text-white font-semibold" data-label={p('col_matricule')}>{employee.mat}</td>}
                    {isColVisible('fullName') && (
                    <td className="px-6 py-4" data-label={p('col_full_name')}>
                      <div>
                        <p className="text-white font-semibold">{employee.prenom} {employee.nom}</p>
                        <p className="text-white/60 text-xs">{employee.dateNaissance ? formatHrDate(employee.dateNaissance, i18n.language) : ''}</p>
                      </div>
                    </td>
                    )}
                    {isColVisible('function') && (
                    <td className="px-6 py-4" data-label={p('col_function')}>
                      <div>
                        <p className="text-white font-medium">{employee.fonction}</p>
                        <p className="text-white/60 text-xs">{employee.posteOccupe}</p>
                      </div>
                    </td>
                    )}
                    {isColVisible('gender') && (
                    <td className="px-6 py-4 text-center" data-label={p('col_gender')}>
                      <div className="flex justify-center">{getGenderBadge(employee.sexe)}</div>
                    </td>
                    )}
                    {isColVisible('situation') && (
                    <td className="px-6 py-4 text-white/80" data-label={p('col_situation')}>
                      <div>
                        <p className="text-white">{employee.situationFamiliale}</p>
                        <p className="text-white/60 text-xs">{p('children_count', { count: employee.enfants || 0 })}</p>
                      </div>
                    </td>
                    )}
                    {isColVisible('contact') && (
                    <td className="px-6 py-4" data-label={p('col_contact')}>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 text-white/80 text-xs">
                          <Phone size={12} />{employee.telephone ? formatHrPhone(employee.telephone) : p('na')}
                        </div>
                        <div className="flex items-center gap-1.5 text-white/60 text-xs">
                          <Mail size={12} />{employee.email || p('na')}
                        </div>
                        <div className="flex items-center gap-1.5 text-white/60 text-xs">
                          <MapPin size={12} />{employee.ville || p('na')}
                        </div>
                      </div>
                    </td>
                    )}
                    {isColVisible('assignment') && <td className="px-6 py-4 text-white/80" data-label={p('col_assignment')}>{employee.affectation}</td>}
                    <td className="px-6 py-4 text-center align-middle" data-label={p('col_actions')}>
                      <QawaemErpRowActions
                        onView={() => handleView(employee)}
                        onEdit={() => handleEdit(employee)}
                        onDelete={() => handleDelete(employee.id)}
                        viewTitle={p('view')}
                        editTitle={p('edit')}
                        deleteTitle={p('delete')}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                    <Users size={48} className="mx-auto mb-4 text-white/30" />
                    <p className="text-lg">{p('empty_state')}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

        {/* Add/Edit Employee Modal */}
        {showModal && ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={closeEmployeeModal}
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              style={{
                boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '90%',
                maxWidth: '1200px',
              }}
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <UserCircle className="text-blue-400" size={28} />
                  {editingEmployee ? p('modal_edit') : p('modal_add')}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    form="employee-info-form"
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                  >
                    <CheckCircle size={18} />
                    {editingEmployee ? p('form_submit_edit') : p('form_submit_add')}
                  </button>
                  <button type="button" onClick={closeEmployeeModal} className="text-white/70 hover:text-white transition-colors">
                    <X size={24} />
                  </button>
                </div>
              </div>

              <HrFormModalTabBar
                tabs={employeeInfoFormTabs}
                activeTab={formTab}
                onTabChange={setFormTab}
                ariaLabel={editingEmployee ? p('modal_edit') : p('modal_add')}
              />

              <div className="overflow-y-auto p-6 flex-1 min-h-0">
                <form id="employee-info-form" onSubmit={(e) => e.preventDefault()}>
                  {formTab === 'personal' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{<RequiredLabelText text={p('form_matricule')} required />}</label>
                        <input type="text" className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={previewHrEmployeeMatricule(hrConfig.employeeReferencePrefix, employees)} required />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{<RequiredLabelText text={p('form_full_name')} required />}</label>
                        <input type="text" className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('ph_full_name')} required />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{<RequiredLabelText text={p('form_function')} required />}</label>
                        <input type="text" className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('ph_function')} required />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{<RequiredLabelText text={p('form_gender')} required />}</label>
                        <select className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                          <option value="">{p('select_placeholder')}</option>
                          <option value="Homme">{p('gender_man')}</option>
                          <option value="Femme">{p('gender_woman')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{<RequiredLabelText text={p('form_situation')} required />}</label>
                        <select className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                          <option value="">{p('select_placeholder')}</option>
                          <option value="single">{p('marital_single')}</option>
                          <option value="married">{p('marital_married')}</option>
                          <option value="divorced">{p('marital_divorced')}</option>
                          <option value="widowed">{p('marital_widowed')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('form_assignment')}</label>
                        <input type="text" className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('ph_assignment')} />
                      </div>
                    </div>
                  )}

                  {formTab === 'details' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{<RequiredLabelText text={p('form_contact')} required />}</label>
                        <input type="tel" className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={phonePlaceholder} required />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_nationality', { defaultValue: t('modules.ressources_humaines.shared.label_nationality_type') })}</label>
                        <select className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">{p('select_placeholder')}</option>
                          <option value="saudi">{t('modules.ressources_humaines.shared.nationality_saudi')}</option>
                          <option value="expatriate">{t('modules.ressources_humaines.shared.nationality_expatriate')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_national_id', { defaultValue: t('modules.ressources_humaines.shared.label_national_id') })}</label>
                        <input type="text" className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('modules.ressources_humaines.shared.ph_national_id')} />
                      </div>
                      <div>
                        <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{p('label_iqama', { defaultValue: t('modules.ressources_humaines.shared.label_iqama') })}</label>
                        <input type="text" className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={t('modules.ressources_humaines.shared.ph_iqama')} />
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </motion.div>
          </div>,
          document.body
        )}

      {showViewModal && viewingEmployee ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={closeViewModal}
          title={p('modal_view_title')}
          icon={Eye}
          testId="employee-info-view-modal"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-white/60 text-sm mb-1">{p('col_matricule')}</p>
                <p className="text-white font-medium">{viewingEmployee.mat || p('na')}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">{p('col_full_name')}</p>
                <p className="text-white font-medium text-lg">{viewingEmployee.prenom} {viewingEmployee.nom}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">{p('col_function')}</p>
                <p className="text-white font-medium">{viewingEmployee.fonction || p('na')}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">{p('export_col_position')}</p>
                <p className="text-white font-medium">{viewingEmployee.posteOccupe || p('na')}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">{p('col_gender')}</p>
                <div>{getGenderBadge(viewingEmployee.sexe)}</div>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">{p('col_situation')}</p>
                <p className="text-white font-medium">{viewingEmployee.situationFamiliale || p('na')}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">{p('col_contact')}</p>
                <p className="text-white font-medium">
                  {viewingEmployee.telephone ? formatHrPhone(viewingEmployee.telephone) : p('na')}
                </p>
                <p className="text-white/60 text-sm mt-1">{viewingEmployee.email || p('na')}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm mb-1">{p('col_assignment')}</p>
                <p className="text-white font-medium">{viewingEmployee.affectation || p('na')}</p>
              </div>
            </div>
          </div>
        </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default EmployeeInfoManagementPage;

